'use client'

import { useState, useEffect } from 'react'
import { TeamGrid } from '@/components/sites/devlg-com-company-page1/TeamGrid'
import { TeamFilterBar } from '@/components/sites/devlg-com-company-page1/TeamFilterBar'
import { getTeams, type Team, type TeamFilters } from '@/lib/api/teams'
import { TeamCardSkeleton } from '@/components/sites/devlg-com-company-page1/TeamCardSkeleton'

// 过滤器状态接口
interface TeamFilterState {
  industryTag: string
  city: string
}

export default function CompanyPage() {
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<TeamFilterState>({
    industryTag: '-1',
    city: '-1'
  })
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 20,
    total_pages: 0
  })

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true)
        setError(null)

        const apiFilters: TeamFilters = {
          industryTag: filters.industryTag === '-1' ? -1 : parseInt(filters.industryTag),
          city: filters.city === '-1' ? -1 : parseInt(filters.city),
          page
        }

        const response = await getTeams(apiFilters)

        setTeams(response.data)
        setPagination(response.pagination)
      } catch (err) {
        console.error('获取团队失败:', err)
        setError('获取团队失败，请稍后重试')
      } finally {
        setLoading(false)
      }
    }

    fetchTeams()
  }, [filters, page])

  const handleFilterChange = (newFilters: TeamFilterState) => {
    setFilters(newFilters)
    setPage(1) // 重置到第一页
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <header className="px-6 py-8 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        <h1 className="text-2xl font-semibold text-gray-900 md:text-3xl">
          软件外包公司团队
        </h1>
      </header>

      {/* Filter Bar */}
      <section className="px-6 pb-6 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        <TeamFilterBar onFilterChange={handleFilterChange} />
      </section>

      {/* Team Grid */}
      <section className="px-6 pb-16 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <TeamCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">加载失败</h3>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无相关团队</h3>
            <p className="text-sm text-gray-500">请尝试调整筛选条件</p>
          </div>
        ) : (
          <>
            <TeamGrid teams={teams} />

            {/* 分页 */}
            {pagination.total_pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>

                <span className="text-sm text-gray-600">
                  第 {page} / {pagination.total_pages} 页
                </span>

                <button
                  onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={page === pagination.total_pages}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
